import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { postAtCounter } from "./guestApi";

/**
 * "🎁 Redeem now — I'm at the counter" on the customer card.
 *
 * The primary path for the gap this closes: a guest opens their card at home,
 * sees a reward is unlocked, and walks in for it. Having ordered nothing today
 * they are invisible to the till, whose "in the room" list is built from
 * orders — so the reward they came for cannot be handed over. Tapping this rings
 * the counter's doorbell and puts their name, highlighted, at the top of the
 * cashier's board.
 *
 * ⚠️ It does NOT redeem. The card is view-only by construction and stays that
 * way — that property is the entire reason a signed token in a URL is an
 * acceptable identity, and a self-redeem button would trade it away. This says
 * "I'm here"; the counter still does the spending. The copy has to carry that
 * honestly, which is why the confirmation reads "we've told the counter, head
 * over" and never "redeemed".
 *
 * It also mints nothing — no points, no visit. Announcing yourself is not a
 * visit; a visit is something a cashier witnesses you pay for.
 *
 * Shown only when a reward is genuinely in reach (`can_redeem` AND affordable),
 * because a doorbell for a redemption the counter would refuse is worse than no
 * doorbell.
 */
export default function AtCounterButton({ token, reward, atCounter, onRaised }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [left, setLeft] = useState(0);

  const expiresAt = atCounter?.expires_at ?? 0;

  // Count the request down so the guest can see it is live and not a button
  // that did nothing. When it lapses the button comes back, which is the honest
  // outcome — nobody came, ring again.
  useEffect(() => {
    if (!expiresAt) { setLeft(0); return undefined; }
    const tick = () => setLeft(Math.max(0, Math.round((expiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const live = expiresAt > 0 && left > 0;

  const raise = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const res = await postAtCounter(token, reward?.id);
      onRaised?.(res);
    } catch (e) {
      const code = e?.response?.data?.error;
      setErr(
        code === "redemption_locked" ? t("card.here.err_locked")
        : code === "nothing_affordable" ? t("card.here.err_nothing")
        : e?.response?.status === 429 ? t("card.here.err_slow")
        : t("card.here.err_generic")
      );
    } finally {
      setBusy(false);
    }
  };

  if (live) {
    return (
      <div className="cc-here done" aria-live="polite">
        <span className="ic" aria-hidden="true">✓</span>
        <div className="tx">
          <div className="hi">{t("card.here.told")}</div>
          <div className="sub">
            {t("card.here.head_over")}
            <span className="cc-here-left mono"> · {mmss(left)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-here">
      <button type="button" className="cc-here-btn" onClick={raise} disabled={busy}>
        {busy ? <span className="ss-spin small" /> : "🎁"} {t("card.here.cta")}
      </button>
      <div className="cc-here-note">
        {reward
          ? t("card.here.note_reward", { reward: reward.label })
          : t("card.here.note")}
      </div>
      {err ? <div className="cc-here-err">{err}</div> : null}
    </div>
  );
}

/** 9:05 — a countdown a guest can read at a glance. */
function mmss(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
